import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { updatePassword, BASE_URL } from '../../services/api'
import '../Login/Login.css' // Re-using Login styles for perfect parity

export default function CreatePassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState('idle') // 'idle' | 'loading' | 'success' | 'error'
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    password: '',
    confirmPassword: ''
  })

  // Extract email/token/companyid from URL
  const email = searchParams.get('email') || ''
  const token = searchParams.get('token') || ''
  const [verifiedCompanyId, setVerifiedCompanyId] = useState(searchParams.get('companyid') || '1')

  useEffect(() => {
    const discoverCompany = async () => {
      if (!email || !token) return;
      
      try {
        const response = await fetch(`${BASE_URL}Pages/ForgetPassword.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            Companyid: verifiedCompanyId,
            Email: email,
            OTP: token
          })
        });

        const data = await response.json();
        const cid = data.Companyid || data.company_id || (data.Response && data.Response.Companyid);
        
        if (cid) {
          console.log("Account identified. CompanyID:", cid);
          setVerifiedCompanyId(String(cid));
          localStorage.setItem('reset_cid', String(cid));
          localStorage.setItem('reset_email', email);
        }
      } catch (err) {
        console.error("Discovery error:", err);
      }
    };

    discoverCompany();
  }, [email, token, verifiedCompanyId]);

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.password || form.password.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setStatus('loading')
    setError('')

    const finalCid = localStorage.getItem('reset_cid') || verifiedCompanyId;
    
    if (!finalCid) {
      setError('Account not identified. Please refresh the page.');
      setStatus('error');
      return;
    }

    const payload = {
      Companyid: finalCid,
      Email: localStorage.getItem('reset_email') || email || "user@petro-genius.com",
      Password: form.password
    };

    console.log("Submitting Create Password Payload:", payload);

    try {
      const res = await updatePassword(payload);

      if (res.success || res.Response === "UPDATED" || res.Response === true) {
        window.location.href = '/'
      } else {
        throw new Error(res.message || 'Failed to update password')
      }
    } catch (err) {
      console.error('Password Creation Error:', err)
      setStatus('error')
      setError(err.message || 'Unable to connect to server')
    }
  }

  if (status === 'success') {
    return (
      <div className="login-page">
        <div className="login-card" style={{ textAlign: 'center', padding: '48px 32px' }}>
          <div style={{ background: '#eefcf5', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', color: '#2d9e5f' }}>
            <CheckCircle2 size={42} />
          </div>
          <h2 className="login-title" style={{ fontSize: '24px', marginBottom: '12px' }}>Password Created!</h2>
          <p className="login-subtitle" style={{ fontSize: '14px', marginBottom: '32px' }}>
            Your account security is now set up. You will be redirected to the login page shortly.
          </p>
          <button className="login-btn" onClick={() => window.location.href = '/'}>
            Go to Login now
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <div className="login-logo">⛽</div>
          <h1 className="login-app-name">Petro Genius</h1>
          <p className="login-tagline">Fuel Station Management System</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <h2 className="login-title" style={{ textAlign: 'center' }}>Security Setup</h2>
          <p className="login-subtitle" style={{ textAlign: 'center' }}>Create a secure password for your company account.</p>

          {error && <div className="login-error">⚠️ {error}</div>}

          <div className="login-field">
            <label style={{ fontSize: '13px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>Email Address</label>
            <input 
              type="email" 
              value={email} 
              readOnly
              style={{ borderRadius: '6px', border: '1px solid #dcdcdc', backgroundColor: '#f7fafd', cursor: 'not-allowed', color: '#5a7286' }}
            />
          </div>

          <div className="login-field">
            <label style={{ fontSize: '13px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>New Password</label>
            <input 
              type="password" 
              placeholder="Minimum 6 characters"
              value={form.password} 
              onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
              style={{ borderRadius: '6px', border: '1px solid #dcdcdc' }}
              required
            />
          </div>

          <div className="login-field">
            <label style={{ fontSize: '13px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>Confirm Password</label>
            <input 
              type="password" 
              placeholder="Repeat your password"
              value={form.confirmPassword} 
              onChange={e => setForm(p => ({ ...p, confirmPassword: e.target.value }))}
              style={{ borderRadius: '6px', border: '1px solid #dcdcdc' }}
              required
            />
          </div>

          <button className="login-btn" disabled={status === 'loading'} style={{ width: '220px', margin: '8px auto 0' }}>
            {status === 'loading' ? <Loader2 className="animate-spin" size={20} /> : 'Finalize Password'}
          </button>
          
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <span onClick={() => window.location.href = '/'} style={{ fontSize: '12px', color: '#1a2b3c', cursor: 'pointer', fontWeight: '800' }}>
              Back to Login
            </span>
          </div>
        </form>
      </div>
    </div>
  )
}
